import { createApi } from '@reduxjs/toolkit/query/react'
import { handleError } from '../../commons/utils/utils'
import { setProviderInfo } from '../slices/providerSlice'
import { generateRPCQuery, getBaseQuery } from './apiUtils'
import apiConstants from './constants'

export const providersApi = createApi({
  reducerPath: 'providersApi',
  refetchOnFocus: false,
  baseQuery: getBaseQuery(),
  tagTypes: ['ProviderDetails', 'ProviderGroups'],
  endpoints: (builder) => ({
    getProviders: builder.query({
      query: (requestParams: CommonSearchParams) =>
        generateRPCQuery(
          apiConstants.PROVIDERS_RPC_PATH,
          'getProvidersForAdmin',
          requestParams
        )
    }),
    updateProvidersUiPositions: builder.mutation({
      query: (requestParams: ProviderUpdateUiParams) =>
        generateRPCQuery(
          apiConstants.PROVIDERS_RPC_PATH,
          'updateUiPositions',
          requestParams
        )
    }),
    getProviderDetail: builder.query({
      query: (requestParams: GetProviderInfoParams) =>
        generateRPCQuery(
          apiConstants.PROVIDERS_RPC_PATH,
          'getProviderByID',
          requestParams
        ),
      providesTags: [{ type: 'ProviderDetails' }],
      async onQueryStarted ({}, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          if (data && data.result) {
            dispatch(setProviderInfo(data.result))
          }
        } catch (err: any) {
          handleError(err, dispatch)
        }
      }
    }),
    getProviderTargetGroups: builder.query({
      query: (requestParams: {
        ProviderID: string
        isExcluded: boolean
        TenantID: string
      }) =>
        generateRPCQuery(
          apiConstants.PROVIDERS_RPC_PATH,
          'getProviderTargetGroups',
          requestParams
        ),
      providesTags: [{ type: 'ProviderGroups' }]
    }),
    addProviderToGroup: builder.mutation({
      query: (requestParams: {
        ProviderID: string
        TargetGroupID: number | string
        Excluded: boolean
        TenantID: string
      }) =>
        generateRPCQuery(
          apiConstants.PROVIDERS_RPC_PATH,
          'addProviderToGroup',
          requestParams
        ),
      invalidatesTags: [{ type: 'ProviderGroups' }]
    }),
    removeProviderFromGroup: builder.mutation({
      query: (requestParams: {
        ProviderID: string
        TargetGroupID: number | string
        TenantID: string
      }) =>
        generateRPCQuery(
          apiConstants.PROVIDERS_RPC_PATH,
          'removeProviderFromGroup',
          requestParams
        ),
      invalidatesTags: [{ type: 'ProviderGroups' }]
    }),
    validateProviderFormFields: builder.mutation({
      query: (requestParams: ProviderFormFields) =>
        generateRPCQuery(
          apiConstants.PROVIDERS_RPC_PATH,
          'validateProviderFormFields',
          requestParams
        )
    }),
    editProvider: builder.mutation({
      query: (requestParams: ProviderFormFields) =>
        generateRPCQuery(
          apiConstants.PROVIDERS_RPC_PATH,
          'editProvider',
          requestParams
        ),
      invalidatesTags: [{ type: 'ProviderDetails' }]
    }),
    fetchProviders: builder.query({
      query: (requestParams: { TenantID: string }) =>
        generateRPCQuery(
          apiConstants.PROVIDERS_RPC_PATH,
          'fetchProviders',
          requestParams
        )
    }),
    getAllProviders: builder.query({
      query: (requestParams: { TenantID: string }) =>
        generateRPCQuery(
          apiConstants.PROVIDERS_RPC_PATH,
          'getAllProviders',
          requestParams
        )
    })
  })
})

export const {
  useGetProviderDetailQuery,
  useGetProviderTargetGroupsQuery,
  useAddProviderToGroupMutation,
  useRemoveProviderFromGroupMutation,
  useValidateProviderFormFieldsMutation,
  useEditProviderMutation,
  useGetProvidersQuery,
  useUpdateProvidersUiPositionsMutation,
  useFetchProvidersQuery,
  useGetAllProvidersQuery
} = providersApi
